import { useState } from "react";
import { Badge, Button, Popover, Space, Tooltip, Typography, theme } from "antd";
import {
  CheckCircleFilled,
  ExclamationCircleFilled,
  TeamOutlined,
  WarningFilled,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { ProcessImpact } from "../../api";
import ProblemList from "./ProblemList";
import ImpactSummary from "./ImpactSummary";
import type { Problem, Selection } from "./types";

const { Text } = Typography;

type Props = {
  problems: Problem[];
  impact: ProcessImpact;
  hasUnpublishedChanges: boolean;
  pendingChanges: string[];
  onSelect: (selection: Selection) => void;
  onOpenLifecycle: () => void;
};

/**
 * What is wrong, and who would notice — as two chips rather than two cards.
 *
 * These used to be permanently expanded panels stacked above the editor, which
 * cost roughly a third of the screen and, worse, greeted every visit with two
 * boxes of warnings before anything had been done. An alarm that is always on
 * is an alarm people learn to look past, and the space it was taking is space
 * the canvas now uses.
 *
 * Nothing is hidden: the counts stay visible at all times and the full list is
 * one click away. What changed is that a clean process now *looks* clean.
 */
const StatusBar = (props: Props) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const [openProblems, setOpenProblems] = useState(false);
  const [openImpact, setOpenImpact] = useState(false);

  const errors = props.problems.filter((p) => p.severity === "error").length;
  const warnings = props.problems.filter((p) => p.severity === "warning").length;
  const clean = props.problems.length === 0;

  return (
    <Space size={8} wrap>
      <Popover
        open={openProblems}
        onOpenChange={setOpenProblems}
        trigger="click"
        placement="bottomLeft"
        overlayStyle={{ maxWidth: 460 }}
        content={
          <div style={{ maxHeight: "50vh", overflowY: "auto" }}>
            <ProblemList
              problems={props.problems}
              onSelect={(selection) => {
                props.onSelect(selection);
                setOpenProblems(false);
              }}
              onOpenLifecycle={() => {
                props.onOpenLifecycle();
                setOpenProblems(false);
              }}
            />
          </div>
        }
      >
        <Button
          size="small"
          type={errors > 0 ? "default" : "text"}
          danger={errors > 0}
          style={
            clean ? { color: token.colorTextTertiary } : undefined
          }
        >
          <Space size={6}>
            {clean ? (
              <>
                <CheckCircleFilled style={{ color: token.colorSuccess }} />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {t("No problems")}
                </Text>
              </>
            ) : (
              <>
                {errors > 0 && (
                  <>
                    <ExclamationCircleFilled style={{ color: token.colorError }} />
                    <span style={{ fontSize: 12 }}>
                      {t("{{count}} to fix", { count: errors })}
                    </span>
                  </>
                )}
                {warnings > 0 && (
                  <>
                    <WarningFilled style={{ color: token.colorWarning }} />
                    <span style={{ fontSize: 12 }}>
                      {t("{{count}} to check", { count: warnings })}
                    </span>
                  </>
                )}
              </>
            )}
          </Space>
        </Button>
      </Popover>

      <Popover
        open={openImpact}
        onOpenChange={setOpenImpact}
        trigger="click"
        placement="bottomLeft"
        overlayStyle={{ maxWidth: 460 }}
        content={
          <ImpactSummary
            impact={props.impact}
            hasUnpublishedChanges={props.hasUnpublishedChanges}
            pendingChanges={props.pendingChanges}
          />
        }
      >
        <Tooltip title={t("Who is mid-case and would notice a change")}>
          <Button size="small" type="text" style={{ color: token.colorTextTertiary }}>
            <Space size={6}>
              <TeamOutlined />
              <span style={{ fontSize: 12 }}>
                {props.impact?.in_flight
                  ? t("{{count}} cases in progress", {
                      count: props.impact.in_flight,
                    })
                  : t("No cases in progress")}
              </span>
              {props.hasUnpublishedChanges && props.pendingChanges.length > 0 && (
                <Badge
                  count={props.pendingChanges.length}
                  size="small"
                  color={token.colorWarning}
                />
              )}
            </Space>
          </Button>
        </Tooltip>
      </Popover>
    </Space>
  );
};

export default StatusBar;
